import { ref } from 'vue'

/**
 * Where the boat is.
 *
 * Asked for only when the reader taps the button, never on the quiet: a permission prompt
 * that appears unbidden is how people learn to press Deny. The last fix is kept, so the
 * list of what is near still works in a harbour with no signal and on the next visit
 * without asking again — with its age shown, since a position from yesterday is a
 * different thing from a position from a minute ago.
 */

export interface Fix {
  lat: number
  lon: number
  /** Metres, as the browser reports it */
  accuracy: number
  at: number
}

export type PositionState = 'idle' | 'asking' | 'ready' | 'denied' | 'unavailable'

const KEY = 'quicktide.position'

function remembered(): Fix | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const fix = JSON.parse(raw) as Fix
    return typeof fix?.lat === 'number' && typeof fix?.lon === 'number' ? fix : null
  } catch {
    return null
  }
}

export const fix = ref<Fix | null>(remembered())
export const state = ref<PositionState>(fix.value ? 'ready' : 'idle')
/** What the browser said when it could not: shown under the notice, so the reason is not a guess */
export const reason = ref('')

/** How long the second attempt may take; a Mac finding itself by Wi-Fi can want most of it */
const PATIENCE_MS = 45_000

function keep(got: GeolocationPosition): void {
  fix.value = {
    lat: got.coords.latitude,
    lon: got.coords.longitude,
    accuracy: got.coords.accuracy,
    at: got.timestamp || Date.now()
  }
  state.value = 'ready'
  reason.value = ''
  try {
    localStorage.setItem(KEY, JSON.stringify(fix.value))
  } catch {
    // a fix that cannot be kept is still a fix for now
  }
}

function failed(error: GeolocationPositionError): void {
  state.value = error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable'
  const name = error.code === error.TIMEOUT ? 'timeout' : error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable'
  reason.value = error.message ? `${name}: ${error.message}` : name
}

/**
 * Two goes at it. The first is a plain request with a short patience, which is all a phone
 * needs. Safari on a Mac often answers that with "position unavailable" or a timeout on the
 * first try and only finds itself once the location service has been left running for a
 * while -- so when the first go fails for any reason but a refusal, the second one watches
 * instead of asking, takes the first fix that comes, and gives up after a good while.
 */
export function ask(): void {
  if (!('geolocation' in navigator)) { state.value = 'unavailable'; reason.value = 'no geolocation in this browser'; return }
  state.value = 'asking'
  reason.value = ''
  navigator.geolocation.getCurrentPosition(
    keep,
    first => {
      if (first.code === first.PERMISSION_DENIED) return failed(first)
      let watch = 0
      let done = false
      const finish = () => { done = true; navigator.geolocation.clearWatch(watch); window.clearTimeout(timer) }
      const timer = window.setTimeout(() => { if (!done) { finish(); failed(first) } }, PATIENCE_MS)
      watch = navigator.geolocation.watchPosition(
        got => { if (!done) { finish(); keep(got) } },
        error => { if (!done && error.code === error.PERMISSION_DENIED) { finish(); failed(error) } },
        { enableHighAccuracy: true, timeout: PATIENCE_MS, maximumAge: 0 }
      )
    },
    // a boat does not move far while this is being answered, and a warm fix is plenty
    { enableHighAccuracy: false, timeout: 15_000, maximumAge: 60_000 }
  )
}

export function forget(): void {
  fix.value = null
  state.value = 'idle'
  try {
    localStorage.removeItem(KEY)
  } catch {
    // nothing to do about it
  }
}

/**
 * Kilometres between two points, flat-earth style.
 *
 * Over the tens of kilometres this is used for, at these latitudes, the error against a
 * great circle is centimetres — and the old app used the same approximation, so the lists
 * come out in the same order they always did.
 */
export function distanceKm(from: { lat: number; lon: number }, to: { lat: number; lon: number }): number {
  const degree = 111.32
  const x = (to.lon - from.lon) * degree * Math.cos(((from.lat + to.lat) / 2) * Math.PI / 180)
  const y = (to.lat - from.lat) * degree
  return Math.hypot(x, y)
}

/** Nearest first, with anything beyond `within` and anything without a position left out */
export function nearest<T extends { lat?: number; lon?: number }>(
  places: T[], here: { lat: number; lon: number }, within: number, most: number
): (T & { km: number })[] {
  const out: (T & { km: number })[] = []
  for (const place of places) {
    if (typeof place.lat !== 'number' || typeof place.lon !== 'number') continue
    const km = distanceKm(here, { lat: place.lat, lon: place.lon })
    if (km <= within) out.push({ ...place, km })
  }
  return out.sort((a, b) => a.km - b.km).slice(0, most)
}
